import React, {useEffect, useState} from "react";
import {KatButton} from "@amzn/katal-react";
import {GetStandingResponsePayload, OdqAndReliabilityApi} from "src/app/api/OdqAndReliabilityApi";

interface StandingCardProps {
    /** Provider id the standing is fetched for. */
    referenceId: string;
    /** ODQ scores DELIVERY opportunities; everything else scores RESERVATION. */
    caseSubType: string;
}

const ODQ_SCORE_LABELS: Record<string, string> = {
    'ON_TIME_DELIVERIES': 'On-time Delivery',
    'DELIVERED_NOT_RECEIVED': 'Delivered and Received',
    'DELIVERY_COMPLETION_RATE': 'Delivery Completion',
    'DELIVERY_ATTEMPTED_RATE': 'Unattempted',
    'UNRETURNED': 'Unreturned',
    'SCAN_COMPLIANCE_RATE': 'Return Scan Compliance',
    'LATE_RETURN': 'Late Return',
};

const RELIABILITY_SCORE_LABELS: Record<string, string> = {
    'RELIABILITY': 'Arrival and Pickup',
    'NO_SHOW': 'On-time Arrival',
    'LATE_CANCEL': 'On-time Cancel',
};

// The provider dashboard shows GOOD as FAIR, so we match it rather than showing a
// different word for the same standing.
const displayBucket = (bucket?: string) => bucket?.toUpperCase() === 'GOOD' ? 'FAIR' : bucket;

const getStandingColor = (bucket?: string): string => {
    if (!bucket) return 'odq-standing--unknown';
    const b = bucket.toUpperCase();
    if (b === 'FANTASTIC' || b === 'GREAT') return 'odq-standing--great';
    if (b === 'FAIR') return 'odq-standing--fair';
    return 'odq-standing--atrisk';
};

/**
 * StandingCard - Overall standing panel with per-score buckets and a Recalculate action.
 * Owns its own GetStanding fetch, so callers pass only the two identifiers it needs.
 */
const StandingCard: React.FC<StandingCardProps> = ({referenceId, caseSubType}) => {
    const [standing, setStanding] = useState<GetStandingResponsePayload | null>(null);
    const [standingLoading, setStandingLoading] = useState(false);
    const [standingError, setStandingError] = useState<string | null>(null);

    const opportunityType = caseSubType === 'ODQ' ? 'DELIVERY' : 'RESERVATION';
    const SCORE_LABELS = caseSubType === 'ODQ' ? ODQ_SCORE_LABELS : RELIABILITY_SCORE_LABELS;

    const fetchStanding = async () => {
        setStandingLoading(true);
        setStandingError(null);
        try {
            const response = await OdqAndReliabilityApi.getStanding(referenceId, opportunityType);
            setStanding(response.data);
        } catch (err: any) {
            setStandingError(err.message || 'Failed to fetch standing');
        } finally {
            setStandingLoading(false);
        }
    };

    useEffect(() => {
        fetchStanding();
    }, [referenceId]);

    return (
        <div className="odq-card" data-testid="odq-standing-card">
            {standingLoading && <div className="odq-standing__value">Loading...</div>}
            {standingError && <div className="odq-standing__value odq-standing--error">Failed to load standing</div>}
            {standing && standing.panelStatus === 'DEGRADED' && (
                <div className="odq-standing__value odq-standing--error">Standing data unavailable (degraded)</div>
            )}
            {standing && standing.panelStatus === 'OK' && (
                <div className="odq-standing__panel">
                    <div className="odq-standing__overall">
                        Overall Standing – <span className={getStandingColor(displayBucket(standing.overallStanding))}>
                            {displayBucket(standing.overallStanding)?.split('_').join(' ') || 'N/A'}
                        </span>
                    </div>

                    <div className="odq-standing__scores">
                        {Object.entries(SCORE_LABELS).map(([scoreType, label]) => {
                            const score = standing.scores?.find(s => s.scoreType === scoreType);
                            const bucket = displayBucket(score?.standingBucket);
                            return (
                                <div className="odq-standing__score-row" key={scoreType}>
                                    <span className="odq-standing__score-label">{label}</span>
                                    <span> – </span>
                                    <span className={getStandingColor(bucket)}>
                                        {bucket?.split('_').join(' ') || 'N/A'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Date Created identifies WHICH snapshot is on screen; Data Freshness
                        advances on every recalculation by design, so it cannot. Both are
                        shown because auditors treat Date Created as their source of truth
                        and had been reading freshness in its place. */}
                    <div className="odq-standing__score-row">
                        <span className="odq-standing__score-label">Date Created (IST):</span>
                        <span> </span>
                        <span data-testid="standing-created-date">{standing.createdDate
                            ? new Date(standing.createdDate).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})
                            : 'N/A'}
                        </span>
                    </div>

                    <div className="odq-standing__score-row">
                        <span className="odq-standing__score-label">Data Freshness Date (IST):</span>
                        <span> </span>
                        <span data-testid="standing-freshness-date">{standing.dataFreshnessDate
                            ? new Date(standing.dataFreshnessDate).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})
                            : 'N/A'}
                        </span>
                    </div>
                </div>
            )}
            <div className="odq-standing__actions">
                <KatButton label="Recalculate Standings" className="odq-calculate-btn"
                           onClick={fetchStanding} disabled={standingLoading}
                           data-testid="recalculate-standings-btn" />
            </div>
        </div>
    );
};

export default StandingCard;
